"use client";

import { Microscope, Flame, Cog, Activity, Crosshair, Wind, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Principles() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const certifications = ["STRATEGIC AUDIT", "BRAND ALIGNMENT", "MARKET ANALYSIS", "CREATIVE DIRECTION"];
  const features = [{
    id: "MD-01",
    icon: Microscope,
    title: "CLARITY FIRST",
    description: "We strip away the noise to reveal the core value proposition of your brand."
  }, {
    id: "MD-02",
    icon: Cog,
    title: "SYSTEMIC DESIGN",
    description: "Marketing is not a campaign; it is a repeatable, scalable engine for growth."
  }, {
    id: "MD-03",
    icon: Flame,
    title: "INTENTIONAL REACH",
    description: "We target the right audience with surgical precision, not broad-spectrum noise."
  }, {
    id: "MD-04",
    icon: Activity,
    title: "DATA INTEGRITY",
    description: "Every decision is backed by rigorous analysis and real-world performance metrics."
  }, {
    id: "MD-05",
    icon: Crosshair,
    title: "PRECISION TUNING",
    description: "Continuous optimization of your brand touchpoints for maximum conversion."
  }, {
    id: "MD-06",
    icon: Wind,
    title: "CULTURAL IMPACT",
    description: "Building brands that resonate deeply within their specific market ecosystem."
  }];
  return <section data-section-id="3990" id="guiding-principles" ref={sectionRef} className="bg-background text-foreground relative overflow-hidden py-24 md:py-24 px-6 md:px-12">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: "linear-gradient(to right, var(--foreground) 1px, transparent 1px), linear-gradient(to bottom, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "40px 40px"
    }} aria-hidden="true" />

      {}
      <div className="absolute top-8 left-6 right-6 md:left-12 md:right-12 flex items-center pointer-events-none" aria-hidden="true">
        <span className="text-primary font-mono text-lg leading-none">+</span>
        <div className="flex-1 border-t border-border mx-2" />
        <span className="text-primary font-mono text-lg leading-none">+</span>
      </div>

      <div className="relative max-w-[1440px] mx-auto pt-8">
        {}
        <div className={`mb-12 md:mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="font-default text-xs md:text-sm text-primary tracking-[0.25em] uppercase font-semibold">
            // 02 — GUIDING PRINCIPLES
          </span>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div className={`transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <h2 className="font-default font-medium text-5xl sm:text-6xl md:text-7xl lg:text-6xl xl:text-7xl uppercase leading-[0.9] tracking-[0.15em] text-foreground">
                  Built to
                  <br />
                  <span className="text-primary">last</span>.
                  <br />
                  Designed to
                  <br />
                  <span className="text-primary">lead</span>.
                </h2>

                <p className="mt-12 text-lg text-ink-muted leading-relaxed max-w-md font-default">
                  Myles Dacres operates at the intersection of creative strategy and disciplined execution. We don't chase trends; we build foundations that allow your brand to command its space.
                </p>

                {}
                <div className="mt-12 space-y-3">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-px w-8 bg-primary" />
                    <span className="font-default text-xs text-ink-muted uppercase tracking-[0.25em]">
                      Core Competencies
                    </span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {certifications.map((cert, i) => <Badge key={cert} data-index={i} variant="outline" className="font-default text-xs border-border text-foreground bg-card px-4 py-2 w-fit tracking-[0.15em]">
                        {cert}
                      </Badge>)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
              {features.map((feature, i) => {
              const Icon = feature.icon;
              return <div key={feature.id} data-index={i} className={`group relative bg-card border border-border hover:border-primary transition-all duration-500 p-8 md:p-10 overflow-hidden ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
                transitionDelay: isVisible ? `${200 + i * 80}ms` : "0ms"
              }}>
                    {}
                    <div className="absolute top-6 left-6 font-default text-xs text-ink-muted tracking-[0.25em]">
                      {feature.id}
                    </div>

                    {}
                    <div className="mt-10 mb-6">
                      <Icon className="w-8 h-8 text-primary group-hover:scale-110 transition-transform duration-500 stroke-[1.5]" aria-hidden="true" />
                    </div>

                    {}
                    <h3 className="font-default font-medium text-lg uppercase tracking-[0.15em] text-foreground mb-4">
                      {feature.title}
                    </h3>

                    {}
                    <p className="text-base text-ink-muted leading-relaxed mb-8 font-default">
                      {feature.description}
                    </p>

                    {}
                    <div className="relative pt-6 mt-auto">
                      <div className="absolute top-0 left-0 w-1/2 h-px border-t border-border group-hover:border-primary transition-colors duration-500" />
                      <Link to="/ServicesPage" className="inline-flex items-center gap-3 font-default text-xs uppercase tracking-[0.25em] text-foreground group-hover:text-primary transition-all duration-500">
                        <span>Explore</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-500" />
                      </Link>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-8 left-6 right-6 md:left-12 md:right-12 flex items-center pointer-events-none" aria-hidden="true">
        <span className="text-primary font-mono text-lg leading-none">+</span>
        <div className="flex-1 border-t border-border mx-2" />
        <span className="text-primary font-mono text-lg leading-none">+</span>
      </div>
    </section>;
}